import React, { useRef } from "react";
import { DHIVEHI_KEY_MAP } from "./dhivehiKeyMap";

interface DhivehiTextFieldProps {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const ALLOWED_KEYS = [
  "Backspace",
  "Delete",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Tab",
  "Enter",
];

export default function DhivehiTextField({
  value,
  onChange,
  placeholder,
  disabled,
}: DhivehiTextFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    // Allow control/navigation keys
    if (ALLOWED_KEYS.includes(e.key)) return;

    // Ignore Ctrl / Alt / Meta combos
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    // 1️⃣ Try exact key (Shift-aware)
    let mappedChar = DHIVEHI_KEY_MAP[e.key];

    // 2️⃣ Fallback to lowercase
    if (!mappedChar) {
      mappedChar = DHIVEHI_KEY_MAP[e.key.toLowerCase()];
    }

    if (!mappedChar) return;

    e.preventDefault();

    const input = inputRef.current;
    if (!input) return;

    const start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? value.length;

    const newValue =
      value.slice(0, start) + mappedChar + value.slice(end);

    onChange(newValue);

    // Restore cursor safely
    requestAnimationFrame(() => {
      const pos = start + mappedChar.length;
      input.setSelectionRange(pos, pos);
    });
  };

  return (
    <input
      ref={inputRef}
      dir="rtl"
      lang="dv"
      value={value}
      onChange={(e) => onChange(e.target.value)} // paste/mobile safe
      onKeyDown={handleKeyDown}
      placeholder={placeholder || "ދިވެހި"}
      disabled={disabled}
      className="
        w-full border rounded-lg px-3 py-2
        text-right text-lg
        font-dhivehi
        focus:ring-2 focus:ring-blue-500
        outline-none
      "
      style={{
        fontFamily: "'Faruma','Mv Iyyu Normal','Noto Sans Thaana',sans-serif",
      }}
    />
  );
}